import { Check, Play, Paperclip, Inbox, Flag } from "lucide-react";

const fmt = (ts) => (ts ? new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);
const first = (n) => (n || "").split(" ")[0];

export function stages(r) {
  const files = r.files?.length || 0;
  const resultsAt = r.files?.find((f) => f.uploaded_at)?.uploaded_at || (files || r.result_url ? r.updated_at : null);
  const done = r.status === "completed";
  const cancelled = r.status === "cancelled";
  return [
    { key: "received", label: "Received", Icon: Inbox, at: r.created_at, who: r.requester_name, done: true },
    { key: "started", label: "Started", Icon: Play, at: r.started_at, who: r.started_by?.name, done: !!r.started_by },
    { key: "results", label: "Results attached", Icon: Paperclip, at: resultsAt, who: null, done: files > 0 || !!r.result_url, count: files },
    { key: "completed", label: cancelled ? "Cancelled" : "Completed", Icon: cancelled ? Flag : Check, at: r.completed_at || (done || cancelled ? r.updated_at : null), who: r.completed_by?.name, done: done || cancelled, cancelled },
  ];
}

export function OwnerChip({ r, size = "sm" }) {
  if (!r.started_by) return <span className="text-[11px] text-slate-400 italic" data-testid={`owner-none-${r.id}`}>Not started</span>;
  const n = r.started_by.name;
  const initials = n.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className="inline-flex items-center gap-1.5" title={`${n} started ${fmt(r.started_at)}`} data-testid={`owner-chip-${r.id}`}>
      <span className={`inline-flex items-center justify-center rounded-full bg-blue-700 text-white font-bold ${size === "sm" ? "h-5 w-5 text-[10px]" : "h-7 w-7 text-xs"}`}>{initials}</span>
      <span className="text-xs text-slate-700">{first(n)}{size !== "sm" && <span className="text-slate-400"> · {fmt(r.started_at)}</span>}</span>
    </span>
  );
}

export function ProgressBar({ r, compact = false }) {
  const st = stages(r);
  const filled = st.filter((s) => s.done).length;
  const cancelled = r.status === "cancelled";
  const tone = cancelled ? "bg-slate-400" : filled === 4 ? "bg-emerald-500" : "bg-blue-600";
  if (compact) {
    return (
      <div className="min-w-[110px]" data-testid={`progress-compact-${r.id}`} title={st.filter((s) => s.done).map((s) => `${s.label}${s.who ? ` · ${first(s.who)}` : ""}${s.at ? ` · ${fmt(s.at)}` : ""}`).join("\n")}>
        <div className="flex gap-0.5">{st.map((s) => <span key={s.key} className={`h-1.5 flex-1 rounded-sm ${s.done ? tone : "bg-slate-200"}`} />)}</div>
        <div className="text-[10px] text-slate-500 mt-0.5 truncate">{cancelled ? "Cancelled" : `${filled}/4 · ${st[filled - 1]?.label}`}</div>
      </div>
    );
  }
  return (
    <div data-testid={`progress-${r.id}`}>
      <div className="flex items-center">
        {st.map((s, i) => (
          <div key={s.key} className="flex items-center flex-1 min-w-0">
            <div className="flex flex-col items-center flex-1 min-w-0 text-center">
              <span className={`h-7 w-7 rounded-full flex items-center justify-center border-2 ${s.done ? `${tone} border-transparent text-white` : "bg-white border-slate-300 text-slate-400"}`} data-testid={`stage-${s.key}-${s.done ? "done" : "todo"}`}><s.Icon className="h-3.5 w-3.5" /></span>
              <div className={`mt-1 text-[11px] font-medium ${s.done ? "text-slate-900" : "text-slate-400"}`}>{s.label}{s.count > 1 ? ` (${s.count})` : ""}</div>
              {s.done && (s.who || s.at) && <div className="text-[10px] text-slate-500 leading-tight">{s.who && <span className="font-medium">{first(s.who)}</span>}{s.who && s.at && " · "}{fmt(s.at)}</div>}
            </div>
            {i < st.length - 1 && <div className={`h-0.5 w-6 -mt-6 shrink-0 ${st[i + 1].done ? tone : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}
